import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type {
  PaginatedResponse,
  SortDirection,
  UserStatus,
  UserWithResolvedScopes,
} from '@klareospace/pharos-lib-types';

import { useDrivers } from './use-drivers';
import type { DriverSortableColumn } from '../types/driver.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseDriversPageOptions {
  carrierId?: string;
  enabled?: boolean;
  /** Locked site (site settings): the list is scoped to this site, like the vehicles list. */
  siteId?: string | null;
  spaceId?: string;
}

export interface UseDriversPageReturn {
  carrierFilter: string[];
  currentPage: number;
  drivers: UserWithResolvedScopes[];
  error: Error | null;
  isLoading: boolean;
  onCarrierChange: (values: string[]) => void;
  onPageChange: (newPage: number) => void;
  onScopeChange: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: DriverSortableColumn, sortDirection: SortDirection | null) => void;
  onStatusChange: (values: string[]) => void;
  pageSize: number;
  scopeValues: string[];
  searchValue: string;
  sortBy: DriverSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  statusValues: UserStatus[];
  totalItems: number;
  totalPages: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toPageData(data: PaginatedResponse<UserWithResolvedScopes> | undefined): {
  drivers: UserWithResolvedScopes[];
  total: number;
  totalPages: number;
} {
  return {
    drivers: data?.items ?? [],
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDriversPage(options?: UseDriversPageOptions): UseDriversPageReturn {
  const searchState = useSearchPageState<DriverSortableColumn>();
  const [statusFilter, setStatusFilter] = useState<UserStatus[]>([]);
  const [scopeFilter, setScopeFilter] = useState<string[]>([]);
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);

  const resolvedCarrierId = options?.carrierId ?? carrierFilter[0];
  // Site settings locks the list to the current site; otherwise the user's scope filter applies.
  const resolvedSiteIds = options?.siteId ? [options.siteId] : scopeFilter.length > 0 ? scopeFilter : undefined;

  const { data, error, isLoading } = useDrivers({
    carrierId: resolvedCarrierId,
    enabled: options?.enabled,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    siteIds: resolvedSiteIds,
    sort: searchState.sort,
    spaceId: options?.spaceId,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
    text: searchState.debouncedSearch,
  });

  function handleStatusChange(values: string[]): void {
    setStatusFilter(values as UserStatus[]);
    searchState.resetPage();
  }

  function handleScopeChange(values: string[]): void {
    setScopeFilter(values);
    searchState.resetPage();
  }

  function handleCarrierChange(values: string[]): void {
    setCarrierFilter(values);
    searchState.resetPage();
  }

  const pageData = toPageData(data);

  return {
    carrierFilter,
    currentPage: searchState.currentPage,
    drivers: pageData.drivers,
    error: error ?? null,
    isLoading,
    onCarrierChange: handleCarrierChange,
    onPageChange: searchState.onPageChange,
    onScopeChange: handleScopeChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onStatusChange: handleStatusChange,
    pageSize: searchState.pageSize,
    scopeValues: scopeFilter,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    statusValues: statusFilter,
    totalItems: pageData.total,
    totalPages: pageData.totalPages,
  };
}
